/* ================================================================
   Ribbon Toolbar - Top toolbar with tabbed groups:
   开始 / 视图 / 面 / 工具 / 设置
   Light theme aligned with the outer ERP shell.
   ================================================================ */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box, Layers, Save, Undo2, Redo2, Download, Upload,
  Eye, EyeOff, Grid3x3, RotateCcw, Hammer, FileCog,
  BookTemplate, X, Plus, Sparkles,
} from 'lucide-react';
import { useEditorStore } from '../state/editorStore';
import type { Layer, ProjectModel, ProjectTemplate, SideId } from '../core/model/types';
import { SIDE_ID_LIST } from '../core/model/types';
import { getSideLabel } from '../core/renderTree';
import { PROJECT_TEMPLATES } from '../core/presets';
import { resolveMaterial } from '../core/materials';
import { designApi } from '@/utils/api';

type RibbonTab = 'start' | 'view' | 'faces' | 'ai' | 'settings';

const TAB_LABELS: Record<RibbonTab, string> = {
  start: '开始',
  view: '视图',
  faces: '面',
  ai: 'AI',
  settings: '设置',
};

const CUSTOM_TEMPLATE_STORAGE_KEY = 'pack3d-custom-project-templates';
const VOLCENGINE_CONFIG_STORAGE_KEY = 'pack3d-volcengine-config';

type VolcengineConfig = {
  apiKey: string;
  baseUrl: string;
  model: string;
};

const DEFAULT_VOLCENGINE_CONFIG: VolcengineConfig = {
  apiKey: 'ark-371985cf-38b1-4631-a5b3-d5470254ea5c-b8083',
  baseUrl: 'https://ark.cn-beijing.volces.com/api/v3',
  model: 'doubao-seedream-4-0-250828',
};

const AI_STYLE_NAMES: Record<number, string> = {
  0: '板条',
  1: '整板',
  3: '田字框架',
  4: '木方/垫块',
};

const AI_SIDE_LABELS: Record<SideId, string> = {
  pedestal: '底座',
  sideFront: '前面',
  sideBack: '后面',
  endLeft: '左端',
  endRight: '右端',
  topCap: '顶盖',
};

function getBoardDirectionLabel(layer: Layer) {
  return layer.direction === 0 ? '纵向铺设' : '横向铺设';
}

function describeLayerForAi(project: ProjectModel, layer: Layer, index: number) {
  const material = resolveMaterial(project, layer.materialKey);
  const materialKind = material.kind === 'plywood'
    ? '胶合板/多层板'
    : material.kind === 'softwood'
      ? '实木软木'
      : material.kind === 'hardwood'
        ? '实木硬木'
        : material.name;

  const boardCount = layer.styleSetting.count.board || layer.blockBoard.count || 0;
  const boardWidth = layer.blockBoard.boardWidth || 0;
  const boardLength = layer.blockBoard.boardLength || 0;

  let detail = `第${index + 1}层: ${AI_STYLE_NAMES[layer.style] || '未知'}，材质 ${material.name}（${materialKind}），厚 ${layer.thickness}mm`;

  if (layer.style === 0) {
    detail += `，${getBoardDirectionLabel(layer)}，由 ${boardCount || 1} 根独立板条并排组成`;
    if (boardWidth > 0) detail += `，单根板条宽 ${boardWidth}mm`;
    if (boardLength > 0) detail += `，单根板条长 ${boardLength}mm`;
    detail += '，必须清晰可见每根板条的分缝，绝不能合并成一整张板';
  } else if (layer.style === 1) {
    detail += `，${getBoardDirectionLabel(layer)}，单张整板覆盖`;
    if (boardLength > 0) detail += `，板长 ${boardLength}mm`;
    detail += '，必须表现为连续完整板面，绝不能拆成多根板条';
  } else if (layer.style === 3) {
    detail += `，竖撑 ${boardCount || 1} 根，横撑 ${layer.styleSetting.count.horBrace?.[0] || 1} 根`;
  } else if (layer.style === 4) {
    detail += `，木方/垫块阵列 ${layer.styleSetting.count.frame?.join('×') || '3×3'}`;
    if (boardWidth > 0) detail += `，木方宽 ${boardWidth}mm`;
    if (boardLength > 0) detail += `，木方长 ${boardLength}mm`;
  }

  if (material.kind === 'plywood') {
    detail += '。表面应接近胶合板质感，纹理均匀、节疤很少，不要生成带大节疤的松木实木外观';
  } else if (material.kind === 'softwood' || material.kind === 'hardwood') {
    detail += '。表面应保持真实木材质感，可有适量木纹、节疤和轻微色差';
  }

  return detail;
}

function buildAiStructureDesc(project: ProjectModel) {
  return project.sides
    .filter(side => side.enabled)
    .map(side => {
      const layers = side.layers.map((layer, index) => describeLayerForAi(project, layer, index)).join('；');
      return `${AI_SIDE_LABELS[side.sideId]}: ${layers}`;
    })
    .join('\n');
}

function buildAiExtraRequirements(project: ProjectModel) {
  const hasStripBoard = project.sides.some(side => side.enabled && side.layers.some(layer => layer.style === 0));
  const hasSingleBoard = project.sides.some(side => side.enabled && side.layers.some(layer => layer.style === 1));
  const hasPlywood = project.sides.some(side => side.enabled && side.layers.some(layer => resolveMaterial(project, layer.materialKey).kind === 'plywood'));

  const requirements = [
    '板型保真优先级高于美观增强，必须严格服从参考 3D 快照的板件分块方式。',
    hasStripBoard ? '凡是板条层，必须保留独立板条数量、板缝、拼接节奏和宽度感，不得偷懒画成一整面连续木板。' : '',
    hasSingleBoard ? '凡是整板层，必须表现为完整连续单板，不得擅自切分成多根板条。' : '',
    hasPlywood ? '凡是胶合板层，表面要接近胶合板/多层板，不要生成明显大节疤、原木瘤结、粗松木拼板效果。' : '',
    '如果参考图某一面能看到板缝，就必须在成图中继续看到对应板缝；如果参考图某一面是整板，就保持完整平整的一张板面。',
  ].filter(Boolean);

  return requirements.join('\n');
}

function loadVolcengineConfig(): VolcengineConfig {
  if (typeof window === 'undefined') return { ...DEFAULT_VOLCENGINE_CONFIG };
  try {
    const raw = window.localStorage.getItem(VOLCENGINE_CONFIG_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_VOLCENGINE_CONFIG };
    return { ...DEFAULT_VOLCENGINE_CONFIG, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_VOLCENGINE_CONFIG };
  }
}

function saveVolcengineConfig(config: VolcengineConfig) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(VOLCENGINE_CONFIG_STORAGE_KEY, JSON.stringify(config));
}

function loadCustomTemplates(): ProjectTemplate[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_TEMPLATE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCustomTemplates(templates: ProjectTemplate[]) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CUSTOM_TEMPLATE_STORAGE_KEY, JSON.stringify(templates));
}

function createTemplatePayload(project: ReturnType<typeof useEditorStore.getState>['project']) {
  return {
    innerSize: project.innerSize,
    outerSize: project.outerSize,
    goodsSize: project.goodsSize,
    payloadKg: project.payloadKg,
    grossWeightKg: project.grossWeightKg,
    symmetry: project.symmetry,
    selfAdaption: project.selfAdaption,
    sideLinkageMode: project.sideLinkageMode,
    sideSpanOverrides: project.sideSpanOverrides,
    sides: project.sides,
    specification: project.specification,
    conditions: project.conditions,
    clearance: project.clearance,
    materialLibrary: project.materialLibrary,
    _legacy: project._legacy,
  };
}

function TemplateLibraryModal({
  templates,
  onClose,
  onApply,
  onSaveCurrent,
}: {
  templates: ProjectTemplate[];
  onClose: () => void;
  onApply: (template: ProjectTemplate) => void;
  onSaveCurrent: (name: string, description: string) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-walnut-900/30 p-6">
      <div className="flex max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-md border border-birch-200 bg-white shadow-pop">
        <div className="flex items-center justify-between border-b border-birch-200 px-5 py-4">
          <div>
            <div className="text-sm font-semibold text-walnut-900">模板库</div>
            <div className="mt-0.5 text-[11px] text-oak-300">点击模板可直接应用，也可以把当前设计保存到模板库。</div>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-oak-300 transition-colors hover:bg-pine-100 hover:text-walnut-700"
            title="关闭"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid flex-1 gap-0 md:grid-cols-[1.4fr_0.9fr]">
          <div className="overflow-y-auto border-r border-birch-200 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {templates.map(t => (
                <button
                  key={t.id}
                  onClick={() => onApply(t)}
                  className="rounded-md border border-birch-200 p-3 text-left transition-colors hover:border-wood-500 hover:bg-wood-100"
                >
                  <div className="text-[13px] font-medium text-walnut-900">{t.name}</div>
                  <div className="mt-1 text-[11px] text-oak-500">{t.description}</div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.scenarioTags.map(tag => (
                      <span key={tag} className="rounded-full bg-pine-100 px-1.5 py-0.5 text-[9px] text-oak-500">
                        {tag}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3 p-4">
            <div>
              <div className="text-sm font-semibold text-walnut-900">保存当前为模板</div>
              <div className="mt-1 text-[11px] text-oak-300">保存后会出现在模板库中，并保存在本机浏览器里。</div>
            </div>

            <label className="block">
              <span className="mb-1 block text-[11px] text-oak-500">模板名称</span>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full rounded-md border border-birch-200 px-3 py-2 text-[12px] focus:border-wood-500 focus:outline-none"
                placeholder="例如：常用出口木箱"
              />
            </label>

            <label className="block">
              <span className="mb-1 block text-[11px] text-oak-500">模板说明</span>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="min-h-[96px] w-full rounded-md border border-birch-200 px-3 py-2 text-[12px] focus:border-wood-500 focus:outline-none"
                placeholder="例如：适合 1200×800×600 的标准出口结构"
              />
            </label>

            <button
              onClick={() => {
                if (!name.trim()) {
                  alert('请输入模板名称');
                  return;
                }
                onSaveCurrent(name.trim(), description.trim());
                setName('');
                setDescription('');
              }}
              className="inline-flex items-center gap-2 rounded-md bg-wood-500 px-4 py-2.5 text-[12px] font-medium text-white hover:bg-wood-700"
            >
              <Plus className="h-4 w-4" />
              保存到模板库
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function VolcengineConfigModal({
  open,
  config,
  onChange,
  onClose,
  onSave,
}: {
  open: boolean;
  config: VolcengineConfig;
  onChange: (patch: Partial<VolcengineConfig>) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted || !open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-walnut-900/35 p-6">
      <div className="w-full max-w-xl rounded-md border border-birch-200 bg-white shadow-pop">
        <div className="flex items-center justify-between border-b border-birch-200 px-5 py-4">
          <div>
            <div className="text-sm font-semibold text-walnut-900">AI配置</div>
            <div className="mt-1 text-[11px] text-oak-500">这些配置只保存在当前浏览器的本地存储里。</div>
          </div>
          <button onClick={onClose} className="rounded-md p-2 text-oak-300 hover:bg-pine-100 hover:text-walnut-700">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4">
          <label className="block">
            <span className="mb-1 block text-[11px] text-oak-500">API Key</span>
            <input
              value={config.apiKey}
              onChange={(e) => onChange({ apiKey: e.target.value })}
              className="w-full rounded-md border border-birch-200 px-3 py-2 text-[12px] focus:border-wood-500 focus:outline-none"
              placeholder="填 sk- 开头的 API Key"
            />
            <div className="mt-1 text-[11px] text-oak-300">这里填`API Key`。</div>
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] text-oak-500">BaseURL</span>
            <input
              value={config.baseUrl}
              onChange={(e) => onChange({ baseUrl: e.target.value })}
              className="w-full rounded-md border border-birch-200 px-3 py-2 text-[12px] focus:border-wood-500 focus:outline-none"
              placeholder="https://ark.cn-beijing.volces.com/api/v3"
            />
            <div className="mt-1 text-[11px] text-oak-300">这里填接口基础地址，默认用 `https://ark.cn-beijing.volces.com/api/v3`。</div>
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] text-oak-500">模型</span>
            <input
              value={config.model}
              onChange={(e) => onChange({ model: e.target.value })}
              className="w-full rounded-md border border-birch-200 px-3 py-2 text-[12px] focus:border-wood-500 focus:outline-none"
              placeholder="doubao-seedream-4-0-250828"
            />
            <div className="mt-1 text-[11px] text-oak-300">这里填生图模型名，默认 `doubao-seedream-4-0-250828`。</div>
          </label>
          <div className="text-[11px] text-warning">
            这种方式适合你自己本机测试，方便一些，但安全性低于只放后端。
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-birch-200 px-5 py-4">
          <button onClick={onClose} className="rounded-md border border-birch-200 px-3 py-2 text-[12px] text-walnut-700 hover:bg-pine-50">
            关闭
          </button>
          <button onClick={onSave} className="rounded-md bg-wood-500 px-3 py-2 text-[12px] font-medium text-white hover:bg-wood-700">
            保存
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// --------------- Building blocks ---------------

function TabButton({
  active, label, onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? 'text-wood-700'
          : 'text-oak-500 hover:text-walnut-900'
      }`}
    >
      {label}
      {active ? (
        <span className="absolute bottom-0 left-1/2 h-0.5 w-6 -translate-x-1/2 rounded-full bg-wood-500" />
      ) : null}
    </button>
  );
}

function RibbonButton({
  icon: Icon, label, onClick, disabled, variant = 'default', title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'primary' | 'danger';
  title?: string;
}) {
  const base = 'flex min-w-[58px] flex-col items-center gap-1 rounded-md px-3 py-2 text-[11px] font-medium transition-colors';
  const color = variant === 'primary'
    ? 'bg-wood-500 text-white hover:bg-wood-700 disabled:bg-wood-100'
    : variant === 'danger'
      ? 'text-danger hover:bg-danger-soft disabled:text-danger'
      : 'text-walnut-700 hover:bg-pine-100 disabled:text-birch-300';
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      title={title ?? label}
      className={`${base} ${color} disabled:cursor-not-allowed`}
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </button>
  );
}

function RibbonSeparator() {
  return <div className="mx-2 h-10 w-px bg-birch-200" />;
}

function RibbonGroup({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-0.5">{children}</div>
      {title ? <div className="mt-1 text-[10px] text-oak-300">{title}</div> : null}
    </div>
  );
}

// --------------- Start tab ---------------

function StartTab() {
  const project = useEditorStore(s => s.project);
  const isDirty = useEditorStore(s => s.isDirty);
  const undoEmpty = useEditorStore(s => s.undoStack.length === 0);
  const redoEmpty = useEditorStore(s => s.redoStack.length === 0);
  const newProject = useEditorStore(s => s.newProject);
  const applyTemplate = useEditorStore(s => s.applyTemplate);
  const saveToLocal = useEditorStore(s => s.saveToLocal);
  const undo = useEditorStore(s => s.undo);
  const redo = useEditorStore(s => s.redo);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [customTemplates, setCustomTemplates] = useState<ProjectTemplate[]>(() => loadCustomTemplates());

  const templates = useMemo(
    () => [...PROJECT_TEMPLATES, ...customTemplates],
    [customTemplates],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('templateLibrary') === '1') {
      setTemplateOpen(true);
    }
  }, []);

  const handleNew = useCallback((mode: 'crate' | 'pallet') => {
    if (isDirty && !confirm('未保存的修改将丢失，是否继续？')) return;
    newProject(mode);
  }, [isDirty, newProject]);

  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = reader.result as string;
          const data = JSON.parse(text);
          if (data.crate || data.sides) {
            useEditorStore.getState().importLegacy(text);
          } else {
            useEditorStore.getState().loadProject(text);
          }
        } catch (e) {
          alert('文件加载失败: ' + (e as Error).message);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  const handleExport = useCallback(() => {
    const json = JSON.stringify(project, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.meta.name || 'project'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [project]);

  return (
    <>
      <RibbonGroup title="新建">
        <RibbonButton icon={Box} label="木箱" onClick={() => handleNew('crate')} />
        <RibbonButton icon={Layers} label="托盘" onClick={() => handleNew('pallet')} />
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonGroup title="从模板">
        <RibbonButton
          icon={BookTemplate}
          label="模板库"
          onClick={() => setTemplateOpen(true)}
        />
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonGroup title="文件">
        <RibbonButton icon={Upload} label="导入" onClick={handleImport} />
        <RibbonButton icon={Download} label="导出" onClick={handleExport} />
        <RibbonButton
          icon={Save}
          label={isDirty ? '保存 *' : '保存'}
          onClick={saveToLocal}
          variant={isDirty ? 'primary' : 'default'}
        />
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonGroup title="历史">
        <RibbonButton icon={Undo2} label="撤销" onClick={undo} disabled={undoEmpty} />
        <RibbonButton icon={Redo2} label="重做" onClick={redo} disabled={redoEmpty} />
      </RibbonGroup>

      {templateOpen ? (
        <TemplateLibraryModal
          templates={templates}
          onClose={() => setTemplateOpen(false)}
          onApply={(template) => {
            if (isDirty && !confirm('未保存的修改将丢失，是否继续？')) return;
            applyTemplate(template);
            setTemplateOpen(false);
          }}
          onSaveCurrent={(name, description) => {
            const template: ProjectTemplate = {
              id: `custom-template-${Date.now()}`,
              name,
              category: project.meta.mode,
              description: description || '自定义模板',
              scenarioTags: ['custom', project.meta.mode],
              payload: createTemplatePayload(project),
            };
            const nextTemplates = [template, ...customTemplates];
            setCustomTemplates(nextTemplates);
            saveCustomTemplates(nextTemplates);
          }}
        />
      ) : null}
    </>
  );
}

// --------------- View tab ---------------

function ViewTab() {
  return (
    <>
      <RibbonGroup title="视图">
        <RibbonButton icon={Eye} label="俯视" onClick={() => dispatchView('top')} />
        <RibbonButton icon={Eye} label="前视" onClick={() => dispatchView('front')} />
        <RibbonButton icon={Eye} label="侧视" onClick={() => dispatchView('side')} />
        <RibbonButton icon={Eye} label="轴测" onClick={() => dispatchView('iso')} />
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonSeparator />
      <RibbonGroup title="辅助">
        <RibbonButton icon={Grid3x3} label="网格" onClick={() => dispatchView('grid')} />
        <RibbonButton icon={Layers} label="拆解" onClick={() => dispatchView('explode')} />
        <RibbonButton icon={RotateCcw} label="复位" onClick={() => dispatchView('reset')} />
      </RibbonGroup>
    </>
  );
}

function dispatchView(kind: string) {
  window.dispatchEvent(new CustomEvent('designer:view', { detail: kind }));
}

// --------------- Faces tab ---------------

function FacesTab() {
  const focusedSideId = useEditorStore(s => s.focusedSideId);
  const setFocusedSide = useEditorStore(s => s.setFocusedSide);
  const toggleSideEnabled = useEditorStore(s => s.toggleSideEnabled);
  const project = useEditorStore(s => s.project);

  return (
    <>
      <RibbonGroup title="聚焦面">
        {SIDE_ID_LIST.map(sid => {
          const isActive = focusedSideId === sid;
          return (
            <button
              key={sid}
              onClick={() => setFocusedSide(isActive ? null : sid)}
              className={`flex min-w-[58px] flex-col items-center gap-1 rounded-md px-3 py-2 text-[11px] font-medium transition-colors ${
                isActive
                  ? 'bg-wood-100 text-wood-700'
                  : 'text-walnut-700 hover:bg-pine-100'
              }`}
            >
              <Box className="h-5 w-5" />
              <span>{getSideLabel(sid)}</span>
            </button>
          );
        })}
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonGroup title="启用">
        {SIDE_ID_LIST.map(sid => {
          const side = project.sides.find(s => s.sideId === sid);
          const enabled = side?.enabled ?? false;
          return (
            <button
              key={sid}
              onClick={() => toggleSideEnabled(sid, !enabled)}
              title={`${enabled ? '禁用' : '启用'} ${getSideLabel(sid)}`}
              className={`h-8 rounded-md px-2 text-[11px] font-medium transition-colors ${
                enabled
                  ? 'bg-success-soft text-success hover:bg-success-soft'
                  : 'bg-pine-100 text-oak-300 hover:bg-birch-200'
              }`}
            >
              {getSideLabel(sid)}
            </button>
          );
        })}
      </RibbonGroup>
    </>
  );
}

// --------------- Settings tab ---------------

function SettingsTab() {
  const project = useEditorStore(s => s.project);
  const setInnerSize = useEditorStore(s => s.setInnerSize);

  const inputClass = 'w-20 rounded-md border border-birch-200 bg-white px-2 py-1 text-xs text-walnut-900 focus:border-wood-500 focus:outline-none';

  return (
    <>
      <RibbonGroup title="内尺寸 (mm)">
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-oak-300">L</span>
          <input
            type="number"
            className={inputClass}
            value={project.innerSize.length}
            onChange={e => setInnerSize({ length: Number(e.target.value) })}
          />
          <span className="text-[10px] text-oak-300">W</span>
          <input
            type="number"
            className={inputClass}
            value={project.innerSize.width}
            onChange={e => setInnerSize({ width: Number(e.target.value) })}
          />
          <span className="text-[10px] text-oak-300">H</span>
          <input
            type="number"
            className={inputClass}
            value={project.innerSize.height}
            onChange={e => setInnerSize({ height: Number(e.target.value) })}
          />
        </div>
      </RibbonGroup>
      <RibbonSeparator />
      <RibbonGroup title="外尺寸 (自动)">
        <div className="text-xs text-oak-500">
          {project.outerSize.length} × {project.outerSize.width} × {project.outerSize.height}
        </div>
      </RibbonGroup>
    </>
  );
}

// --------------- Main Toolbar ---------------

export default function Toolbar() {
  const [tab, setTab] = useState<RibbonTab>('start');
  const [volcengineRendering, setVolcengineRendering] = useState(false);
  const [volcengineConfigOpen, setVolcengineConfigOpen] = useState(false);
  const [volcengineConfig, setVolcengineConfig] = useState<VolcengineConfig>(() => loadVolcengineConfig());
  const [previewImages, setPreviewImages] = useState<Array<{ label: string; url: string }>>([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  const project = useEditorStore(s => s.project);
  const renderCount = useEditorStore(s => s.renderNodes.length);

  const handleSaveVolcengineConfig = useCallback(() => {
    saveVolcengineConfig(volcengineConfig);
    setVolcengineConfigOpen(false);
    alert('配置已保存');
  }, [volcengineConfig]);

  const captureViewSnapshots = useCallback((): Promise<Array<{ label: string; url: string }>> => {
    return new Promise((resolve) => {
      const views = [
        { label: '轴测', kind: 'iso' },
        { label: '前视', kind: 'front' },
        { label: '侧视', kind: 'side' },
        { label: '俯视', kind: 'top' },
      ];
      const results: Array<{ label: string; url: string }> = [];
      let idx = 0;

      function captureNext() {
        if (idx >= views.length) {
          // 恢复轴测视角
          window.dispatchEvent(new CustomEvent('designer:view', { detail: 'iso' }));
          resolve(results);
          return;
        }
        const view = views[idx];
        window.dispatchEvent(new CustomEvent('designer:view', { detail: view.kind }));
        // 等相机动画完成再截图
        setTimeout(() => {
          const canvas = document.querySelector('.box-designer-viewport canvas') as HTMLCanvasElement | null;
          if (canvas) {
            results.push({ label: view.label, url: canvas.toDataURL('image/png', 0.95) });
          }
          idx++;
          captureNext();
        }, 400);
      }

      captureNext();
    });
  }, []);

  const handleVolcengineRender = useCallback(async () => {
    if (!volcengineConfig.apiKey.trim()) {
      setVolcengineConfigOpen(true);
      alert('请先填写 API Key');
      return;
    }

    const canvas = document.querySelector('.box-designer-viewport canvas') as HTMLCanvasElement | null;
    if (!canvas) {
      alert('没有找到当前 3D 视图，无法截图');
      return;
    }

    setVolcengineRendering(true);
    try {
      // 先截四视图作为 AI 参考
      const snapshots = await captureViewSnapshots();
      const snapshotUrls = snapshots.map(s => s.url);

      const structureDesc = buildAiStructureDesc(project);
      const extraRequirements = buildAiExtraRequirements(project);

      const response = await designApi.renderVolcengineImage({
        snapshot: snapshotUrls[0],
        image: snapshotUrls,
        mode: project.meta.mode,
        outerLength: project.outerSize.length,
        outerWidth: project.outerSize.width,
        outerHeight: project.outerSize.height,
        renderCount,
        structureDesc,
        extraRequirements,
        apiKey: volcengineConfig.apiKey.trim(),
        baseUrl: volcengineConfig.baseUrl.trim(),
        model: volcengineConfig.model.trim(),
      });

      const imageUrl = (response as any)?.imageUrl;
      if (!imageUrl) throw new Error('没有返回图片地址');

      setPreviewImages([{ label: 'AI 实物图', url: imageUrl }]);
      setPreviewOpen(true);
    } catch (error) {
      alert(`火山引擎出图失败：${(error as any)?.message || '未知错误'}`);
    } finally {
      setVolcengineRendering(false);
    }
  }, [captureViewSnapshots, project.meta.mode, project.outerSize.height, project.outerSize.length, project.outerSize.width, renderCount, volcengineConfig.apiKey, volcengineConfig.baseUrl, volcengineConfig.model]);

  return (
    <div className="flex-shrink-0 border-b border-birch-200 bg-white">
      {/* Tab header */}
      <div className="flex items-center justify-between border-b border-birch-200 px-3">
        <div className="flex items-center gap-1">
          <div className="mr-4 flex items-center gap-2 pr-3">
            <Hammer className="h-4 w-4 text-wood-500" />
            <span className="text-[13px] font-semibold text-walnut-900">
              {project.meta.mode === 'pallet' ? '托盘设计' : '木箱设计'}
            </span>
          </div>
          {(Object.keys(TAB_LABELS) as RibbonTab[]).map(t => (
            <TabButton
              key={t}
              active={tab === t}
              label={TAB_LABELS[t]}
              onClick={() => setTab(t)}
            />
          ))}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-oak-500">
          <span className="inline-flex items-center gap-1">
            <FileCog className="h-3.5 w-3.5" />
            {renderCount} 个构件
          </span>
        </div>
      </div>

      {/* Tab body */}
      <div className="flex min-h-[86px] items-start gap-1 overflow-x-auto px-3 py-2">
        {tab === 'start' && <StartTab />}
        {tab === 'view' && <ViewTab />}
        {tab === 'faces' && <FacesTab />}
        {tab === 'ai' && (
          <>
            <RibbonGroup title="AI 出图">
              <RibbonButton
                icon={Sparkles}
                label={volcengineRendering ? '生成中...' : '实物出图'}
                onClick={handleVolcengineRender}
                disabled={volcengineRendering}
                variant="primary"
              />
            </RibbonGroup>
            <RibbonSeparator />
            <RibbonGroup title="配置">
              <RibbonButton
                icon={FileCog}
                label="引擎配置"
                onClick={() => setVolcengineConfigOpen(true)}
              />
            </RibbonGroup>
          </>
        )}
        {tab === 'settings' && <SettingsTab />}
      </div>

      <VolcengineConfigModal
        open={volcengineConfigOpen}
        config={volcengineConfig}
        onChange={(patch) => setVolcengineConfig(prev => ({ ...prev, ...patch }))}
        onClose={() => setVolcengineConfigOpen(false)}
        onSave={handleSaveVolcengineConfig}
      />

      {previewOpen && previewImages.length > 0 && createPortal(
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-walnut-900/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-md border border-birch-200 bg-white shadow-pop">
            <div className="flex items-center justify-between border-b border-birch-200 px-5 py-3">
              <div className="text-sm font-semibold text-walnut-900">预览 · {previewImages.length} 张</div>
              <button
                onClick={() => setPreviewOpen(false)}
                className="rounded-md p-2 text-oak-300 hover:bg-pine-100 hover:text-walnut-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div className={`grid gap-4 ${previewImages.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 lg:grid-cols-3'}`}>
                {previewImages.map((img, idx) => (
                  <div key={idx} className="group relative overflow-hidden rounded-md border border-birch-200">
                    <img
                      src={img.url}
                      alt={img.label}
                      className="w-full object-contain"
                      style={{ maxHeight: previewImages.length <= 2 ? 420 : 300 }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-ironwood/60 px-3 py-2">
                      <span className="text-[12px] font-medium text-white">{img.label}</span>
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = img.url;
                          link.download = `shenlu-${img.label}-${Date.now()}.png`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="rounded-md bg-white px-2 py-1 text-[10px] font-medium text-walnut-700 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        下载
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-birch-200 px-5 py-3">
              <button
                onClick={() => {
                  previewImages.forEach((img) => {
                    const link = document.createElement('a');
                    link.href = img.url;
                    link.download = `shenlu-${img.label}-${Date.now()}.png`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  });
                }}
                className="rounded-md bg-wood-500 px-4 py-2 text-[12px] font-medium text-white hover:bg-wood-700"
              >
                全部下载
              </button>
              <button
                onClick={() => setPreviewOpen(false)}
                className="rounded-md border border-birch-200 px-4 py-2 text-[12px] font-medium text-walnut-700 hover:bg-pine-50"
              >
                关闭
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
